/**
 * @author Artem Nakhodkin <artem.nakhodkin@racingpost.com>
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { connect } from 'react-redux';
import classNames from 'classnames';

import { authReady } from 'rp-navigation';
import Svg from 'rp-react-svg-icon';
import rpMqBreakpoints, { breakpointCodes } from 'rp-mq-breakpoints';
import { getPrefValue, CARDS_PREFERRED_TAB } from 'rp-url-navigation-prefs';
import CardsMatrix from './components/CardsMatrix';
import CardsMeetingsBtn from './components/CardsMeetingsBtn';
import CardsError from './components/CardsError';

const CARDS_PRO_TAB = 'pro';
const CARDS_FREE_TAB = 'free';

/**
 * Component that is used for rendering cards
 */

class Cards extends Component {
    static get defaultProps() {
        return {
            meetingsMaxAmountFromMedium: 6,
            courseHeight: 64,
            abandonedHeight: 46,
        };
    }

    static propTypes = {
        /**
         * Limit of amount cards shown on medium screen
         */
        meetingsMaxAmountFromMedium: PropTypes.number.isRequired,
        /**
         * Height of course line
         */
        courseHeight: PropTypes.number.isRequired,
        /**
         * Height of abandoned course line
         */
        abandonedHeight: PropTypes.number.isRequired,
        /**
         * Races date
         */
        date: PropTypes.string.isRequired,
        /**
         * Races date heading
         */
        heading: PropTypes.string.isRequired,
        /**
         * Races date heading short
         */
        shortHeading: PropTypes.string.isRequired,
        /**
         * List of courses
         */
        courses: PropTypes.arrayOf(
            PropTypes.shape({
                /**
                 * Unique id
                 */
                id: PropTypes.oneOfType([
                    PropTypes.number,
                    PropTypes.string
                ]).isRequired,
                /**
                 * Is course abandoned
                 */
                abandoned: PropTypes.bool.isRequired,
                /**
                 * Is Course all Weather
                 */
                allWeather: PropTypes.bool.isRequired,
                /**
                 * Surface type of AW track
                 */
                surfaceType: PropTypes.string,
                /**
                 * Course colour
                 * If there is no colour, there will be no specific class
                 */
                colour: PropTypes.number,
                /**
                 * Course name
                 */
                name: PropTypes.string.isRequired,
                /**
                 * Course country code
                 */
                countryCode: PropTypes.string.isRequired,
                /**
                 * Course meeting url
                 */
                meetingUrl: PropTypes.string.isRequired,
                /**
                 * Course hashname
                 */
                hashName: PropTypes.string.isRequired,
                /**
                 * Course type
                 */
                meetingTypeCode: PropTypes.string.isRequired,
                /**
                 * List of races
                 */
                races: PropTypes.arrayOf(
                    PropTypes.shape({
                        /**
                         * Is race abandoned
                         */
                        abandoned: PropTypes.bool.isRequired,
                        /**
                         * Is Race resulted
                         */
                        result: PropTypes.bool.isRequired,
                        /**
                         * Race link
                         */
                        link: PropTypes.string.isRequired,
                        /**
                         * Race title
                         */
                        title: PropTypes.string.isRequired,
                        /**
                         * Race time
                         */
                        time: PropTypes.oneOfType([
                            PropTypes.string,
                            PropTypes.number
                        ]).isRequired,
                        /**
                         * Race number of runners
                         */
                        runners: PropTypes.number.isRequired,
                        /**
                         * Has race offers
                         */
                        hasRaceOffers: PropTypes.bool.isRequired,
                        /**
                         * Is next race
                         */
                        isNextRace: PropTypes.bool.isRequired,
                        /**
                         * Is next race less than 30 min away
                         */
                        isThirthyMinToNextRace: PropTypes.bool.isRequired,
                        /**
                         * Is Race started
                         */
                        started: PropTypes.bool.isRequired,
                    })
                )
            })
        ).isRequired
    };

    state = {
        isAnchorSet: false,
        prefTab: '',
        isExpanded: false,
        currentBreakpointCode: rpMqBreakpoints.current().code,
        matrixEpandedHeight: this.getMatrixHeight(true),
        matrixCollapsedHeight: this.getMatrixHeight(false),
    };

    componentDidMount() {
        authReady.then((auth) => {
            this.updateCardLinks(auth.user.isLoggedIn);
        });

        window.addEventListener('rpBreakpointsSwitch', this.setCurrentBreakpointCode);
    }

    componentWillUnmount() {
        window.removeEventListener('rpBreakpointsSwitch', this.setCurrentBreakpointCode);
    }

    setCurrentBreakpointCode = (event) => {
        const code = event.detail.breakpoint.code;
        this.setState({
            currentBreakpointCode: code,
        });
    };

    getMatrixHeight(isExpanded) {
        const { courses, meetingsMaxAmountFromMedium, courseHeight, abandonedHeight } = this.props;
        const numberOfCourses = isExpanded ? courses.length : meetingsMaxAmountFromMedium;
        let matrixHeight = 0;

        for (let i = 0; i < numberOfCourses; i += 1) {
            if (courses[i] && !courses[i].abandoned) {
                matrixHeight += courseHeight;
            } else {
                matrixHeight += abandonedHeight;
            }
        }

        return matrixHeight;
    }

    /**
     * Method that is used for rendering date alias:
     * Today's or Tomorrow's, if it exists
     * @returns {string}
     */
    getTitle = () => {
        const { shortHeading, heading } = this.props;
        const { EXTRA_NARROW, NARROW } = breakpointCodes;
        return [EXTRA_NARROW, NARROW].includes(
            this.state.currentBreakpointCode) ?
            shortHeading :
            heading;
    };

    isMatrixExpandable = () => {
        const { currentBreakpointCode } = this.state;
        const { courses, meetingsMaxAmountFromMedium } = this.props;
        const { NARROW, EXTRA_NARROW } = breakpointCodes;

        return [NARROW, EXTRA_NARROW].indexOf(currentBreakpointCode) > -1
            ? false
            : courses.length > meetingsMaxAmountFromMedium;
    };

    updateCardLinks = (isLoggedIn) => {
        const prefTab = getPrefValue(CARDS_PREFERRED_TAB, CARDS_FREE_TAB);
        this.setState({
            isAnchorSet: prefTab !== CARDS_PRO_TAB || isLoggedIn,
            prefTab,
        });
    };

    /**
     * Method that is used for toggling meetings
     */
    toggleMeetings = () => {
        if (this.state.isExpanded) {
            this.hideMeetings();
        } else {
            this.showMeetings();
        }
    };

    hideMeetings = () => {
        const matrixNode = document.getElementById('cardsMatrix');
        matrixNode.setAttribute('style', `max-height:${this.state.matrixCollapsedHeight}px`);

        this.setState({
            isExpanded: false
        });
    };

    showMeetings = () => {
        const matrixNode = document.getElementById('cardsMatrix');

        matrixNode.setAttribute('style', `max-height:${this.state.matrixEpandedHeight}px`);

        this.setState({
            isExpanded: true
        });
    };

    render() {
        const { date, courses } = this.props;
        const {
            isAnchorSet,
            isExpanded, prefTab,
            matrixCollapsedHeight,
            currentBreakpointCode
        } = this.state;
        const cardsClass = classNames(
            'rh-cards',
            {
                'rh-cards--expanded': isExpanded,
            }
        );
        const isExpandable = this.isMatrixExpandable();

        return (
            courses.length > 0 ?
                <section className={cardsClass} id="cardsBlock">
                    <div className="rh-cardsHeader">
                        <h2 className="rh-cardsHeader__title">{this.getTitle()}</h2>
                        <div className="rh-cardsHeader__linkContainer">
                            <a
                                className={classNames(
                                    'rh-cardsHeader__link',
                                    'ui-link',
                                    'ui-link_table',
                                    'js-popupLink')}
                                href={`/non-runners/${date}`}
                            >
                                Going and non-runners
                                <Svg
                                    className="ui-icon ui-icon__btn"
                                    iconName="right"
                                    size="Small"
                                />
                            </a>
                            <a
                                className={classNames(
                                    'rh-cardsHeader__link',
                                    'ui-link',
                                    'ui-link_table',
                                    'js-navigate-url')}
                                href="/racecards/next-race"
                            >
                                Next race off
                                <Svg
                                    className="ui-icon ui-icon__btn"
                                    iconName="right"
                                    size="Small"
                                />
                            </a>
                        </div>
                    </div>
                    <CardsMatrix
                        courses={courses}
                        date={date}
                        isAnchorSet={isAnchorSet}
                        prefTab={prefTab}
                        height={matrixCollapsedHeight}
                        isExpandable={isExpandable}
                        currentBreakpointCode={currentBreakpointCode}
                    />
                    {isExpandable
                    && (<CardsMeetingsBtn
                        onClick={this.toggleMeetings}
                        isExpanded={isExpanded}
                    />)
                    }
                </section> : <CardsError />
        );
    }
}

const mapStateToProps = ({ cards: { courses, date: { shortHeading, heading, date } } }) => ({
    courses,
    date,
    shortHeading,
    heading
});

const ConnectedCards = connect(mapStateToProps)(Cards);

export {
    Cards,
    ConnectedCards as default
};



// WEBPACK FOOTER //
// ./src/js/Cards/Cards.js